import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './EditBudgetStructure.css';
import IconButton from '@mui/material/IconButton';
import axiosInstance from '../../../Config/axiosInstance';
import Select from 'react-select';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function EditBudgetStructure() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [formData, setFormData] = useState({
    offerCode: '',
    projectCode: '',
    elementCostId: '',
    elementCostDescription: '',
  });

  const [projectCodes, setProjectCodes] = useState([]);
  const [offerCodes, setOfferCodes] = useState([]);

  // Fetch offer codes from API
  useEffect(() => {
    axiosInstance.get('/offer-codes/')
      .then(response => {
        console.log('Offer Codes:', response.data);
        if (response.data && Array.isArray(response.data)) {
          setOfferCodes(response.data.map(code => ({ value: code.id, label: code.offer_code })));
        } else {
          console.error('Offer Codes structure is not valid:', response.data);
        }
      })
      .catch(error => console.error("Error fetching offer codes:", error));
  }, []);

  // Fetch project codes from API
  useEffect(() => {
    axiosInstance.get('/project-codes/')
      .then(response => {
        console.log('Project Codes:', response.data);
        setProjectCodes(response.data.map(code => ({ value: code.id, label: code.code })));
      })
      .catch(error => console.error("Error fetching project codes:", error));
  }, []);

  // Fetch data for the form based on the provided ID
  useEffect(() => {
    if (state?.id) {
      const fetchData = async () => {
        try {
          const response = await axiosInstance.get(`/budget-structure/${state.id}/`);
          let record = response.data;

          console.log('Fetched Record:', record);

          if (Array.isArray(response.data)) {
            record = response.data.find(item => item.id === state.id);
          }

          if (record) {
            setFormData({
              offerCode: record.offer_code_details?.id || '',
              projectCode: record.project_code_details?.id || '',
              elementCostId: record.cost_element_id || '',
              elementCostDescription: record.cost_element_description || '',
            });
          } else {
            console.error("No record found with id:", state.id);
          }
        } catch (error) {
          console.error("Error fetching data:", error.response ? error.response.data : error.message);
        }
      };
      fetchData();
    }
  }, [state?.id]);

  const [errors, setErrors] = useState({
    offerCode: '',
    elementCostId: '',
    elementCostDescription: '',
  });

  const validateForm = () => {
    let newErrors = {};

    if (!formData.offerCode) {
      newErrors.offerCode = "Offer code is required.";
    }
    if (!formData.elementCostId) {
      newErrors.elementCostId = "Cost id is required.";
    }
    if (!formData.elementCostDescription) {
      newErrors.elementCostDescription = "Cost description is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();  
    if (!validateForm()) return;
    try {
      const updatedData = {
        offer_code: formData.offerCode,
        project_code: formData.projectCode,
        cost_element_id: formData.elementCostId,
        cost_element_description: formData.elementCostDescription,
      };

      await axiosInstance.put(`/budget-structure/${state.id}/`, updatedData);
      navigate(`/project-budget/budget-structure`);
    } catch (error) {
      console.error('Error saving data:', error.response ? error.response.data : error.message);
    }
  };

  const handleDiscard = () => {
    navigate(`/project-budget/budget-structure`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleUndo = () => {
    navigate(-1);
  };

  return (
    <div className='edit-budget-page'>
      <div className="edit-container">
        <div className="header">
          <IconButton onClick={handleUndo}>
            <ArrowBackIcon className="back-arrow" />
          </IconButton>
          <h2 className="edit-title">Edit budget structure</h2>
        </div>
      </div>

      <hr className="title-separator" />
      <div className="edit-container">
        <form onSubmit={handleSave} className="edit-form">
          {/* Offer Code */}
          <div className="form-group">
            <label htmlFor="offer_code" className="label">
              Offer Code <span style={{ color: 'red' }}>*</span>
            </label>
            {offerCodes.length > 0 && (
              <Select
                id="offer_code"
                name="offerCode"
                classNamePrefix="react-select"
                options={offerCodes}
                value={offerCodes.find(c => c.value === formData.offerCode) || null}
                onChange={selected => {
                  setFormData({ ...formData, offerCode: selected ? selected.value : '' });
                }}
                isClearable
                styles={{ control: (provided) => ({ ...provided, borderRadius: '8px' }) }}
                required
              />
            )}
            {errors.offerCode && <div className="error">{errors.offerCode}</div>}
          </div>

          {/* Project Code */}
          <div className="form-group">
            <label htmlFor="project_code" className="label">
              Project Code 
            </label>
            <Select
              id="project_code"
              name="projectCode"
              classNamePrefix="react-select"
              options={projectCodes}
              value={projectCodes.find(c => c.value === formData.projectCode) || null}
              onChange={selected => {
                setFormData({ ...formData, projectCode: selected ? selected.value : '' });
              }}
              isClearable
              styles={{ control: (provided) => ({ ...provided, borderRadius: '8px' }) }}
            />
            {errors.projectCode && <div className="error">{errors.projectCode}</div>}
          </div>

          {/* Cost id */}
          <div className="form-group">
            <label htmlFor="cost_element_id" className="label">
              Cost ID <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              id="cost_element_id"
              name="elementCostId"
              value={formData.elementCostId}
              onChange={handleChange}
              className="input"
              required
            />
            {errors.elementCostId && <div className="error">{errors.elementCostId}</div>}
          </div>

          {/* Cost Description */}
          <div className="form-group">
            <label htmlFor="cost_element_description" className="label">
              Cost description <span style={{ color: 'red' }}>*</span>
            </label>
            <textarea
              id="cost_element_description"
              name="elementCostDescription"
              value={formData.elementCostDescription}
              onChange={handleChange}
              className="textarea"
              required
            />
            {errors.elementCostDescription && <div className="error">{errors.elementCostDescription}</div>}
          </div>

          {/* Buttons */}
          <div className="form-group-row">
            <div className="button-group-inline">
              <button type="button" className="button discard" onClick={handleDiscard}>
                Discard
              </button>
              <button type="submit" className="button save">
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditBudgetStructure;
